import { useEffect, useState } from "react";
import { api, type DebugMode, type PatientDetail } from "../api";
import { errorText } from "../hooks";

type Option = "skip_checks" | "report_on_screen" | "report_pdf";

const OPTIONS: Array<{ key: Option; label: string; hint: string }> = [
  {
    key: "skip_checks", label: "Skip the monitor checks",
    hint: "Home sessions skip the screen, monitor and brightness checks, on any computer with Chrome. The screen size is "
      + "estimated and the screen is not calibrated, so results are labelled debug and never enter the history or change alerts.",
  },
  {
    key: "report_on_screen", label: "Show the comprehensive report on screen",
    hint: "The patient's results page gets a Full report tab: the screen check, geometry, codes, calibration, brightness "
      + "readings and target, staircases and every trial, with the trials CSV.",
  },
  {
    key: "report_pdf", label: "Comprehensive report as PDF",
    hint: "The patient can download the Debug PDF from their results page.",
  },
];

/** Admins' per-patient debug mode: a switch, then what it should do. Saved on each click. */
export function DebugModeCard({ p, isAdmin, onChange }: { p: PatientDetail; isAdmin: boolean; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // shown at once, then as saved (a failed save goes back to what the server has)
  const [d, setD] = useState<DebugMode | null>(p.debug ?? null);
  useEffect(() => setD(p.debug ?? null), [p.debug]);
  const save = async (enabled: boolean, opts: Partial<Record<Option, boolean>> = {}) => {
    const next = { skip_checks: false, report_on_screen: false, report_pdf: false,
      ...(d ? { skip_checks: d.skip_checks, report_on_screen: d.report_on_screen, report_pdf: d.report_pdf } : {}),
      ...opts };
    setD(enabled ? { ...d, ...next } : null);
    setBusy(true);
    setError(null);
    try {
      const saved = await api.setDebugMode(p.id, { enabled, ...next });
      setD(saved.debug ?? null);
      onChange();
    } catch (e) {
      setD(p.debug ?? null);
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const ticked = OPTIONS.filter((o) => d?.[o.key]);
  return (
    <div className={`card ${d ? "demo-card" : ""}`}>
      <h2 style={{ marginTop: 0 }}>
        <label style={{ flexDirection: "row", alignItems: "center", gap: "0.5rem", display: "inline-flex" }}>
          <input type="checkbox" aria-label="Debug mode" checked={!!d} disabled={!isAdmin || busy}
            onChange={(e) => void save(e.target.checked)} />
          Debug mode
        </label>{" "}
        {d ? <span className="badge demo">on</span> : <span className="muted small">off</span>}
      </h2>
      {d ? (
        <>
          <p className="small muted" style={{ marginTop: 0 }}>
            Turned on by {d.by ?? "an admin"}{d.since ? `, ${new Date(d.since).toLocaleDateString()}` : ""}.
            {ticked.length === 0 && " Tick what it should do for this patient."}
          </p>
          <div className="debug-options">
            {OPTIONS.map((o) => (
              <label key={o.key} className="check-row">
                <input type="checkbox" aria-label={o.label} checked={d[o.key]} disabled={!isAdmin || busy}
                  onChange={(e) => void save(true, { [o.key]: e.target.checked })} />
                <span><strong>{o.label}</strong><br /><span className="small muted">{o.hint}</span></span>
              </label>
            ))}
          </div>
          {d.skip_checks && <p className="small">Use a demonstration patient, not a study participant.</p>}
        </>
      ) : (
        <p className="small muted">For demonstrations and trying things out: skip this patient's monitor checks, and show
          them the comprehensive report on screen or as a PDF. Admins turn it on.</p>
      )}
      {error && <div className="error">{error}</div>}
    </div>
  );
}
